import { Component, HostBinding, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LocalLogin } from '../../api/models/local-login';
import { AuthService } from '../../core/security/auth.service';
import { routerFlipTransition } from '../../core/animations/router.animations';
import { MatSwitchCompanyDialogComponent } from '../../dialogs/mat-switch-company-dialog/mat-switch-company-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { NotificationService } from '../../notification/notification.service';
import { CookieService } from 'ngx-cookie-service';
import { Title } from '@angular/platform-browser';
import { NgIf } from '@angular/common';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatPasswordInputComponent } from '../../editors/mat-password-input/mat-password-input.component';
import { MatButton, MatAnchor } from '@angular/material/button';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { FlexModule } from '@angular/flex-layout';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    animations: [routerFlipTransition()],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NgIf,
        MatTextInputComponent,
        MatPasswordInputComponent,
        MatButton,
        Angular2PromiseButtonModule,
        MatAnchor,
        FlexModule,
    ],
})
export class LoginComponent implements OnChanges {
  @HostBinding('@routerTransition') routerTransition = '';

  form: UntypedFormGroup;
  forgotForm: UntypedFormGroup;
  submitted: boolean;
  submittedReset: boolean;
  serverError: boolean;
  verifyTokenError: boolean;
  resetSuccess: boolean;
  twoFactorSecondStep = false;
  twoFactorSource = '';
  twoFactorTokenHint = '';

  showForgotPassword = false;
  buttonSubmitPromise: Promise<boolean>;
  resetButtonSubmitPromise: Promise<boolean>;
  currentYear: number;

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private cookieService: CookieService,
    private titleService: Title,
  ) {
    this.titleService.setTitle('Customer Portal');
    this.currentYear = new Date().getFullYear();

    this.createForm();
    // const module = RouterHelper.getRouteByComponent('SignUpComponent');
    // this.signupUrl = module.path;
  }

  createForm(): void {
    this.form = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      token: ['', []],
    });

    this.forgotForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnChanges() {}

  async onSubmit() {
    this.serverError = false;
    this.verifyTokenError = false;

    if (this.form.valid) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<boolean> {
    this.submitted = true;
    if (this.twoFactorSecondStep) {
      const token = this.form.value.token;
      if (await this.authService.verifyToken(token)) {
        const currentUser = this.authService.currentUser.getValue();
        await this.proceedAfterSuccessfulLogin(currentUser);
      } else {
        this.verifyTokenError = true;
        this.submitted = false;
      }

      return true;
    }

    const reqData = this.prepareFormModel();
    if (await this.authService.login(reqData)) {
      const currentUser = this.authService.currentUser.getValue();
      if (currentUser.twoFactorNextStepRequested) {
        this.form.controls['token'].setValidators(Validators.required);
        this.twoFactorSecondStep = currentUser.twoFactorNextStepRequested;
        this.twoFactorSource = currentUser.twoFactorSource;
        if (currentUser.twoFactorProvider == 'authenticator') {
          this.twoFactorTokenHint = 'One time code you can find in Authenticator app';
        } else if (currentUser.twoFactorProvider == 'phoneNumber') {
          this.twoFactorTokenHint = `One time code was sent to ${this.maskData(PhoneNumbersUtils.format(currentUser.twoFactorSource), 0, 4)}`;
        } else if (currentUser.twoFactorProvider == 'email') {
          this.twoFactorTokenHint = `One time code was sent to ${this.maskData(currentUser.twoFactorSource)}`;
        }
      } else {
        await this.proceedAfterSuccessfulLogin(currentUser);
      }

      this.submitted = false;
    } else {
      this.serverError = true;
      this.submitted = false;
    }

    return true;
  }

  maskData(maskStr: string, frontChars = 1, backChars = 4) {
    return maskStr.replace(/./g, function (c, i, a) {
      return i >= frontChars && i <= a.length - backChars - 1 ? '*' : c;
    });
  }

  async proceedAfterSuccessfulLogin(currentUser) {
    if (currentUser && currentUser.accounts.length > 1) {
      const selectedCompany = !!this.cookieService.get('x-selected-company');
      if (!selectedCompany) {
        const dialogRef = this.dialog.open(MatSwitchCompanyDialogComponent, {
          width: '400px',
          disableClose: true,
        });

        dialogRef.afterClosed().subscribe(async (closeCause: any) => {
          console.log(`Close cause: ${closeCause}`);
          await this.authService.redirectAfterLogin();
        });
      }
    } else {
      await this.authService.redirectAfterLogin();
    }
  }

  prepareFormModel(): LocalLogin {
    const formModel = this.form.value;

    return {
      password: formModel.password as string,
      username: formModel.username as string,
    };
  }

  /*revert() {
    this.submitted = false;
    this.form.reset({});
  }*/

  revertReset() {
    this.submittedReset = false;
    this.twoFactorSecondStep = false;
    this.forgotForm.reset({});
  }

  forgotPasswordClick() {
    this.showForgotPassword = !this.showForgotPassword;
  }

  async onForgotPasswordSubmit() {
    this.submittedReset = true;
    this.serverError = false;
    this.resetSuccess = false;

    if (this.forgotForm.valid) {
      this.resetButtonSubmitPromise = this.processResetSubmit();
    }
  }

  async processResetSubmit(): Promise<boolean> {
    const reqData = this.forgotForm.value.username;

    if (await this.authService.resetPassword(reqData)) {
      this.revertReset();
      this.showForgotPassword = false;
      this.resetSuccess = true;
    } else {
      this.serverError = true;
    }

    return true;
  }

  resendSmsCode() {
    this.authService
      .resendOtp()
      .then(value => {
        this.notificationService.notifySuccess(value);
      })
      .catch(err => {
        console.log(err);
      });
  }
}
